/**
 * signup-destination v0.2 (T1082, 2026-10-27): a row for /geo-check.
 * signup-destination v0.1 (T534, cc_tasks row 494, 2026-10-07)
 *
 * What a stranger gets for the page they were sent to /auth from, in their words.
 * Used by the sign-up screen (the line over the form) and by the "Check your email"
 * panel ("The link brings you straight back to <what>"). One small table: a page with
 * no row gets the plain fallback. No prices, no counts, no internal names.
 */
export interface SignupDestinationCopy {
  /** The line over the Create account form. */
  line: string;
  /** What the confirmation link returns them to, mid-sentence. */
  what: string;
}

const FALLBACK: SignupDestinationCopy = {
  line: "Create a free account to continue",
  what: "the page you asked for",
};

// First match wins: more specific paths first.
const TABLE: Array<[RegExp, SignupDestinationCopy]> = [
  [/^\/accelerator-vetter(?:[/?#]|$)/, { line: "Create a free account to vet an accelerator", what: "the accelerator you were about to vet" }],
  [/^\/company-vetter(?:[/?#]|$)/, { line: "Create a free account to vet a company", what: "the company you were about to vet" }],
  [/^\/dashboard\/vetting-suite(?:[/?#]|$)/, { line: "Create a free account to run your check", what: "the check you started" }],
  [/^\/dashboard\/find(?:[/?#]|$)/, { line: "Create a free account to find the people you are looking for", what: "your people search" }],
  [/^\/dashboard\/press(?:[/?#]|$)/, { line: "Create a free account to see press requests on your topic", what: "the press requests you asked for" }],
  [/^\/dashboard\/pipeline-crm(?:[/?#]|$)/, { line: "Create a free account to draft your first message", what: "the message you were about to draft" }],
  [/^\/funding\//, { line: "Create a free account to watch this company", what: "the company you were looking at" }],
  [/^\/\?(?:.*&)?run=/, { line: "Create a free account to keep your free check", what: "your free check" }],
  // T1082 (2026-10-27): the free GEO check sends people here with next=/geo-check?domain=<theirs>.
  [/^\/geo-check(?:[/?#]|$)/, { line: "Create a free account to keep your GEO check", what: "your GEO check result" }],
];

export function signupDestinationCopy(next: string | null | undefined): SignupDestinationCopy | null {
  if (!next) return null;
  for (const [re, copy] of TABLE) if (re.test(next)) return copy;
  return FALLBACK;
}

/**
 * Has this browser ever signed in to MentionFox? A marker written on every successful
 * sign-in and never removed at sign-out (auth-helpers storeLastAuthMethod), plus the
 * older last-method key for browsers that signed in before the marker existed.
 */
export const SIGNED_IN_BEFORE_KEY = "mf_signed_in_before";
export function browserHasSignedInHere(): boolean {
  try {
    return !!(localStorage.getItem(SIGNED_IN_BEFORE_KEY) || localStorage.getItem("brand_monitor_last_auth_method"));
  } catch {
    return true; // storage unreadable: keep today's sign-in screen
  }
}
